package org.teddapp.project.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.server.AboutExtras
import org.teddapp.project.server.AboutLink
import org.teddapp.project.server.ArticleMeta
import org.teddapp.project.server.HomeExtras
import org.teddapp.project.server.ServerPublication
import org.teddapp.project.server.ServerSeries
import org.teddapp.project.sync.ServerIds
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddFilterChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge

// ──────────────────────────────────────────────────────────────────────
// Publier sur le site : un contenu de l'appareil, ou une page rédigée ici.
// Le site est en français et en anglais ; chaque version s'écrit à la main,
// et on relie les deux par « Traduction de ».
// ──────────────────────────────────────────────────────────────────────

/** Le formulaire ouvert dans une rubrique du site. */
internal sealed interface PublicationForm {
    /**
     * Publier une note, une pièce de portfolio ou une compétence. Ouvert depuis une
     * rubrique, le type est fixé ; depuis une langue manquante, la langue et la version
     * à relier le sont aussi.
     */
    data class Content(
        val kind: String = "note",
        val lang: String = "fr",
        val translationOf: String? = null,
        val lockKind: Boolean = false,
        val lockLang: Boolean = false,
        /** Déjà en ligne : on le republie, avec sa présentation. */
        val existing: ServerPublication? = null,
    ) : PublicationForm

    /**
     * Rédiger une page. [existing] absent : nouvelle page, à la place [role] et dans la
     * langue [lang] ; [lockPlace] fige les deux quand la rubrique les impose.
     */
    data class Page(
        val existing: ServerPublication?,
        val role: String? = null,
        val lang: String = "fr",
        val translationOf: String? = null,
        val lockPlace: Boolean = false,
    ) : PublicationForm
}

/** Un élément de l'appareil qu'on peut publier, avec l'identifiant qu'il porte sur le serveur. */
internal data class LocalSource(val kind: String, val serverId: String, val title: String, val localId: String = "")

internal const val NOT_FOUND = "Introuvable sur le serveur."

/** Publier un élément que le serveur n'a pas encore reçu échoue : on dit quoi faire. */
internal fun publishError(message: String): String =
    if (message == NOT_FOUND) "Cet élément n'est pas encore sur le serveur. Synchronise, puis réessaie." else message

internal fun kindLabel(kind: String) = when (kind) {
    "note" -> "Note"
    "portfolio_piece" -> "Pièce de portfolio"
    "competence" -> "Compétence"
    "page" -> "Page"
    else -> kind
}

internal fun roleLabel(role: String?) = when (role) {
    "home" -> "Accueil"
    "about" -> "À propos"
    else -> "Page libre"
}

internal val LANGS = listOf("fr" to "Français", "en" to "English")

internal fun langName(lang: String) = if (lang == "en") "anglais" else "français"

internal suspend fun loadLocalSources(repos: TeddRepositories?): List<LocalSource> = withContext(Dispatchers.Default) {
    val db = repos?.db ?: return@withContext emptyList()
    fun id(local: String) = if (ServerIds.isUuid(local)) local.lowercase() else ServerIds.derive(local)
    // Le site ne montre pas les compétences : on ne propose plus de les publier.
    db.noteQueries.selectAll().executeAsList().map { LocalSource("note", id(it.id), it.title.orEmpty().ifBlank { "Sans titre" }, it.id) } +
        db.portfolioPieceQueries.selectAll().executeAsList().map { LocalSource("portfolio_piece", id(it.id), it.title.ifBlank { "Sans titre" }, it.id) }
}

/**
 * « Écrire un serveur ! » → `ecrire-un-serveur` : ce que le serveur accepte comme adresse.
 * [typing] garde un tiret final : sans cela, on ne pourrait pas en taper un au clavier.
 * Il est retiré à l'envoi.
 */
internal fun slugify(text: String, typing: Boolean = false): String {
    val plain = buildString {
        for (c in text.lowercase()) append(
            when (c) {
                'à', 'â', 'ä', 'á', 'ã' -> 'a'
                'ç' -> 'c'
                'é', 'è', 'ê', 'ë' -> 'e'
                'î', 'ï', 'í' -> 'i'
                'ô', 'ö', 'ó', 'õ' -> 'o'
                'ù', 'û', 'ü', 'ú' -> 'u'
                'ÿ' -> 'y'
                'ñ' -> 'n'
                else -> if (c in 'a'..'z' || c in '0'..'9') c else '-'
            },
        )
    }
    val joined = plain.replace(Regex("-+"), "-").trimStart('-').take(80)
    return if (typing) joined else joined.trimEnd('-')
}

@Composable
internal fun OpenForm(model: AdminModel) {
    val publications = model.publications.orEmpty()
    when (val form = model.form) {
        null -> Unit
        is PublicationForm.Content -> PublishForm(model, form, publications)
        is PublicationForm.Page -> PageForm(model, form, publications)
    }
}

@Composable
internal fun PublicationCard(model: AdminModel, p: ServerPublication, compact: Boolean) {
    val details = "/${p.lang}/${p.slug} · ${kindLabel(p.source_kind)} · ${formatDay(p.published_at_ms)}"
    val state: @Composable () -> Unit = {
        TeddTag(p.lang.uppercase())
        if (p.online) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne")
        else TeddTag(if (p.isPage) "Brouillon" else "Retirée")
    }
    val actions: @Composable () -> Unit = {
        TeddButton(text = "Aperçu", onClick = { model.openPreview(p) }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, icon = "eye")
        if (p.isPage) TeddButton(text = "Modifier", onClick = { model.openPage(p) }, size = TeddButtonSize.Sm, enabled = !model.busy)
        if (p.online) TeddButton(text = "Retirer", onClick = { model.confirmUnpublish = p }, size = TeddButtonSize.Sm)
        else if (!p.isPage) TeddButton(text = "Remettre en ligne", onClick = { model.republish(p) }, size = TeddButtonSize.Sm, enabled = !model.busy)
    }
    val text: @Composable (Modifier) -> Unit = { m ->
        Column(modifier = m, verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(p.title, color = if (p.online) TeddTokens.Ink else TeddTokens.InkMuted, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(details, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
    TeddCard(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(TeddTokens.RMd), padding = PaddingValues(horizontal = TeddSpace.S4, vertical = TeddSpace.S3)) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                text(Modifier.fillMaxWidth())
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    state()
                    Box(Modifier.weight(1f))
                    actions()
                }
            }
        } else {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                text(Modifier.weight(1f))
                state()
                actions()
            }
        }
    }
}

// ── Morceaux de formulaire ─────────────────────────────────────────────

@Composable
internal fun FormLabel(text: String) = Text(text, color = TeddTokens.Ink, style = TeddType.Label)

@Composable
internal fun Hint(text: String) = Text(text, color = TeddTokens.InkMuted, style = TeddType.Caption)

@Composable
internal fun <T> Choice(label: String, options: List<Pair<T, String>>, selected: T, onSelect: (T) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        FormLabel(label)
        Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            options.forEach { (value, text) -> TeddFilterChip(label = text, selected = value == selected, onClick = { onSelect(value) }) }
        }
    }
}

/** Une liste où l'on choisit une ligne ; elle défile au-delà de quelques lignes. */
@Composable
private fun <T> PickList(items: List<T>, selected: T?, label: (T) -> String, detail: (T) -> String?, onSelect: (T) -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(max = 208.dp)
            .clip(shape)
            .border(1.dp, TeddTokens.Line, shape)
            .verticalScroll(rememberScrollState()),
    ) {
        items.forEachIndexed { index, item ->
            if (index > 0) Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            val on = item == selected
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface)
                    .clickable { onSelect(item) }
                    .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
            ) {
                Text(label(item), color = TeddTokens.Ink, style = TeddType.Body, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                detail(item)?.let { Text(it, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp)) }
                if (on) TeddIcon(name = "check", size = 14.dp, color = TeddTokens.Ink)
            }
        }
    }
}

/** L'autre version du même contenu, si elle est déjà en ligne : le site reliera les deux. */
@Composable
private fun TranslationPicker(candidates: List<ServerPublication>, selected: String?, onSelect: (String?) -> Unit) {
    if (candidates.isEmpty()) return
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        FormLabel("Traduction de (optionnel)")
        Hint("Choisis la version déjà publiée dans l'autre langue : le site proposera de passer de l'une à l'autre.")
        PickList(
            items = candidates,
            selected = candidates.firstOrNull { it.id == selected },
            label = { it.title },
            detail = { "/${it.lang}/${it.slug}" },
            onSelect = { onSelect(if (it.id == selected) null else it.id) },
        )
    }
}

@Composable
private fun PublishForm(model: AdminModel, preset: PublicationForm.Content, publications: List<ServerPublication>) {
    val existing = preset.existing
    var kind by remember(preset) { mutableStateOf(preset.kind) }
    var source by remember(preset) {
        mutableStateOf(existing?.let { LocalSource(it.source_kind, it.source_id, it.title) })
    }
    var filter by remember(preset) { mutableStateOf("") }
    var lang by remember(preset) { mutableStateOf(preset.lang) }
    var slug by remember(preset) { mutableStateOf(existing?.slug.orEmpty()) }
    var translationOf by remember(preset) { mutableStateOf(preset.translationOf) }
    val article = rememberArticleState(preset, publications)
    val items = model.sources.filter { it.kind == kind && it.title.contains(filter.trim(), ignoreCase = true) }
    TeddCard(
        modifier = Modifier.widthIn(max = 640.dp).fillMaxWidth(),
        title = when {
            existing != null -> "Modifier « ${existing.title} »"
            preset.translationOf != null -> "Ajouter la version " + if (lang == "en") "anglaise" else "française"
            kind == "portfolio_piece" -> "Publier une pièce de portfolio" + if (preset.lockLang) " · ${langName(lang)}" else ""
            kind == "note" -> "Publier un article" + if (preset.lockLang) " · ${langName(lang)}" else ""
            else -> "Publier un contenu"
        },
        icon = "globe",
        padding = PaddingValues(TeddSpace.S5),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            Hint(
                if (existing != null) "Republier reprend le texte actuel de la note, avec la présentation ci-dessous."
                else "Le site reçoit une copie figée. Modifier l'original ne change rien au site tant que tu ne republies pas.",
            )
            if (!preset.lockKind) Choice(
                label = "Type",
                options = listOf("note" to "Note", "portfolio_piece" to "Pièce de portfolio"),
                selected = kind,
                onSelect = { kind = it; source = null; translationOf = null },
            )
            if (existing == null) Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddField(label = "Élément", value = filter, onValueChange = { filter = it }, placeholder = "Filtrer par titre")
                if (items.isEmpty()) Hint(if (filter.isBlank()) "Rien de ce type sur cet appareil." else "Aucun titre ne correspond.")
                else PickList(items, source, label = { it.title }, detail = { null }) {
                    source = it
                    slug = slugify(it.title)
                }
            }
            if (!preset.lockLang) Choice(label = "Langue de ce contenu", options = LANGS, selected = lang, onSelect = { lang = it; translationOf = null })
            TeddField(label = "Adresse sur le site", value = slug, onValueChange = { slug = slugify(it, typing = true) }, placeholder = "mon-article")
            if (slug.isNotEmpty()) Hint(
                when (kind) {
                    "note" -> "/$lang/blog/${slugify(slug)}"
                    "portfolio_piece" -> "/$lang/portfolio/${slugify(slug)}"
                    else -> "/$lang/${slugify(slug)}"
                },
            )
            if (kind == "note") ArticleFields(article, lang)
            if (preset.translationOf == null && existing == null) TranslationPicker(
                candidates = publications.filter { it.online && it.source_kind == kind && it.lang != lang },
                selected = translationOf,
                onSelect = { translationOf = it },
            )
            FormError(model.formError)
            Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(
                    text = "Aperçu",
                    onClick = {
                        val meta = if (kind == "note") article.meta() else null
                        val doc = PreviewDoc(
                            kind = kind, lang = lang, slug = slugify(slug).ifEmpty { "adresse" }, title = source?.title.orEmpty(), body = "",
                            subtitle = meta?.subtitle.orEmpty(), summary = meta?.summary.orEmpty(), cover = meta?.cover.orEmpty(), series = meta?.series,
                            draft = true,
                        )
                        val src = source
                        if (src != null && src.localId.isNotEmpty()) model.previewSource(src, doc)
                        else if (existing != null) model.preview = previewOf(existing).copy(subtitle = doc.subtitle, summary = doc.summary, cover = doc.cover, series = doc.series, draft = true)
                    },
                    kind = TeddButtonKind.Ghost,
                    icon = "eye",
                    enabled = source != null,
                )
                TeddButton(
                    text = if (model.busy) "Publication…" else if (existing != null) "Republier" else "Publier",
                    onClick = {
                        source?.let { model.publish(it, slugify(slug), lang, translationOf, if (kind == "note") article.meta() else null) }
                    },
                    kind = TeddButtonKind.Publish,
                    enabled = !model.busy && source != null && slug.isNotEmpty() && (kind != "note" || article.valid),
                )
                TeddButton(text = "Annuler", onClick = { model.form = null }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

/** Ce qu'on saisit pour présenter un article : sous-titre, résumé, couverture, série. */
internal class ArticleState(
    subtitle: String,
    summary: String,
    cover: String,
    seriesKey: String?,
    seriesTitle: String,
    episode: String,
    /** Les séries déjà publiées : clé, titres par langue, dernier épisode par langue. */
    val known: List<KnownSeries>,
) {
    var subtitle by mutableStateOf(subtitle)
    var summary by mutableStateOf(summary)
    var cover by mutableStateOf(cover)
    /** Clé de la série choisie, [NEW_SERIES] pour une nouvelle, null pour aucune. */
    var seriesKey by mutableStateOf(seriesKey)
    var seriesTitle by mutableStateOf(seriesTitle)
    var episode by mutableStateOf(episode)

    private val inSeries get() = seriesKey != null
    val valid: Boolean
        get() = !inSeries || (seriesTitle.isNotBlank() && episode.toIntOrNull() in 1..99 && slugify(key()).isNotEmpty())

    private fun key() = if (seriesKey == NEW_SERIES) slugify(seriesTitle) else seriesKey.orEmpty()

    fun meta() = ArticleMeta(
        subtitle = subtitle.trim(),
        summary = summary.trim(),
        cover = cover.trim(),
        series = if (inSeries) ServerSeries(key(), seriesTitle.trim(), episode.toIntOrNull() ?: 1) else null,
    )

    companion object {
        /** Jamais une clé : une clé de série est un slug. */
        const val NEW_SERIES = "+nouvelle"
    }
}

internal data class KnownSeries(val key: String, val titles: Map<String, String>, val lastEpisode: Map<String, Int>)

/** Les séries que connaissent les articles publiés, la plus récente d'abord. */
internal fun knownSeries(publications: List<ServerPublication>): List<KnownSeries> =
    publications.filter { it.series != null }
        .sortedByDescending { it.published_at_ms }
        .groupBy { it.series!!.key }
        .map { (key, pubs) ->
            KnownSeries(
                key = key,
                titles = pubs.groupBy { it.lang }.mapValues { (_, l) -> l.first().series!!.title },
                lastEpisode = pubs.groupBy { it.lang }.mapValues { (_, l) -> l.maxOf { it.series!!.episode } },
            )
        }

@Composable
private fun rememberArticleState(preset: PublicationForm.Content, publications: List<ServerPublication>): ArticleState =
    remember(preset) {
        val existing = preset.existing
        fun field(name: String) = existing?.content?.get(name)?.runCatching { jsonPrimitive.contentOrNull }?.getOrNull().orEmpty()
        // La série : celle de l'article, sinon celle de la version qu'on traduit.
        val series = existing?.content?.get("series")?.let { runCatching { kotlinx.serialization.json.Json.decodeFromJsonElement(ServerSeries.serializer(), it) }.getOrNull() }
            ?: publications.firstOrNull { it.id == preset.translationOf }?.series
        val known = knownSeries(publications)
        val sameLang = series?.let { s -> known.firstOrNull { it.key == s.key }?.titles?.get(preset.lang) }
        ArticleState(
            subtitle = field("subtitle"),
            summary = field("summary"),
            cover = field("cover"),
            seriesKey = series?.key,
            // Traduire un épisode : le titre de la série reste à écrire dans cette langue, s'il ne l'est pas déjà.
            seriesTitle = if (existing != null) series?.title.orEmpty() else sameLang.orEmpty(),
            episode = series?.episode?.toString().orEmpty(),
            known = known,
        )
    }

@Composable
private fun ArticleFields(state: ArticleState, lang: String) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
        FormLabel("Présentation de l'article")
        TeddField(label = "Sous-titre (optionnel)", value = state.subtitle, onValueChange = { state.subtitle = it.take(160) }, placeholder = "Ce que l'article apporte, en une ligne")
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            FormLabel("Résumé (optionnel)")
            Hint("Deux ou trois phrases, montrées dans le catalogue du blog. Sans résumé, le site prend le début du texte.")
            TextArea(state.summary, { state.summary = it.take(400) }, placeholder = "De quoi parle l'article, et pour qui.", minHeight = 80.dp)
        }
        TeddField(
            label = "Image de couverture (optionnel)",
            value = state.cover,
            onValueChange = { state.cover = it.trim() },
            keyboardType = androidx.compose.ui.text.input.KeyboardType.Uri,
            placeholder = "https://…",
        )
        val options = buildList<Pair<String?, String>> {
            add(null to "Aucune")
            state.known.forEach { s -> add(s.key to (s.titles[lang] ?: s.titles.values.first())) }
            add(ArticleState.NEW_SERIES to "Nouvelle série")
        }
        Choice(
            label = "Série",
            options = options,
            selected = state.seriesKey,
            onSelect = { key ->
                state.seriesKey = key
                val known = state.known.firstOrNull { it.key == key }
                state.seriesTitle = known?.titles?.get(lang).orEmpty()
                state.episode = if (key == null) "" else ((known?.lastEpisode?.get(lang) ?: 0) + 1).toString()
            },
        )
        if (state.seriesKey != null) Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            TeddField(
                label = "Titre de la série · ${langName(lang)}",
                value = state.seriesTitle,
                onValueChange = { state.seriesTitle = it.take(120) },
                placeholder = "Construire une synchronisation",
                modifier = Modifier.weight(1f),
            )
            TeddField(
                label = "Épisode",
                value = state.episode,
                onValueChange = { v -> state.episode = v.filter(Char::isDigit).take(2) },
                keyboardType = androidx.compose.ui.text.input.KeyboardType.Number,
                placeholder = "1",
                modifier = Modifier.widthIn(max = 96.dp),
            )
        }
    }
}

/** « 8 ans | d'expérience », une ligne par chiffre : les quatre premières lignes bien formées. */
internal fun parseStats(text: String): List<Pair<String, String>> =
    text.lines().mapNotNull { line ->
        val value = line.substringBefore('|').trim()
        val label = line.substringAfter('|', "").trim()
        if (value.isEmpty() || label.isEmpty()) null else value.take(60) to label.take(60)
    }.take(4)

/** Les chiffres d'une page rouverte, remis sous la forme qu'on saisit. */
internal fun statLines(page: ServerPublication?): String =
    (page?.content?.get("stats") as? kotlinx.serialization.json.JsonArray).orEmpty().mapNotNull { stat ->
        val o = stat as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
        val value = o["value"]?.jsonPrimitive?.contentOrNull ?: return@mapNotNull null
        val label = o["label"]?.jsonPrimitive?.contentOrNull ?: return@mapNotNull null
        "$value | $label"
    }.joinToString("\n")

@Composable
private fun PageForm(model: AdminModel, preset: PublicationForm.Page, publications: List<ServerPublication>) {
    val existing = preset.existing
    fun field(name: String) = existing?.content?.get(name)?.runCatching { jsonPrimitive.contentOrNull }?.getOrNull()
    var title by remember(existing?.id) { mutableStateOf(existing?.title.orEmpty()) }
    // Une page d'accueil ou À propos créée depuis sa rubrique reçoit l'adresse attendue.
    val placeSlug = when (preset.role) {
        "home" -> if (preset.lang == "en") "home" else "accueil"
        "about" -> if (preset.lang == "en") "about" else "a-propos"
        else -> null
    }
    var slug by remember(preset) { mutableStateOf(existing?.slug ?: placeSlug.orEmpty()) }
    // Tant qu'on n'a pas touché à l'adresse d'une nouvelle page, elle suit le titre.
    var slugEdited by remember(preset) { mutableStateOf(existing != null || placeSlug != null) }
    var lang by remember(preset) { mutableStateOf(existing?.lang ?: preset.lang) }
    var role by remember(preset) { mutableStateOf(if (existing != null) field("role") else preset.role) }
    var body by remember(existing?.id) { mutableStateOf(field("body").orEmpty()) }
    var translationOf by remember(preset) { mutableStateOf(preset.translationOf) }
    // Propres à l'accueil : les chiffres de ses tuiles, la présentation de l'appli, le bandeau de contact.
    var stats by remember(existing?.id) { mutableStateOf(statLines(existing)) }
    val extras = remember(existing?.id) { HomeState(homeExtrasOf(existing)) }
    val contact = remember(existing?.id) { AboutState(aboutExtrasOf(existing)) }
    val home = role == "home"
    val about = role == "about"
    val save = { published: Boolean ->
        model.savePage(
            existing?.id, slugify(slug), lang, title, body, role, translationOf, published,
            stats = if (home || about) parseStats(stats) else emptyList(),
            home = if (home) extras.value() else null,
            about = if (about) contact.value() else null,
        )
    }
    TeddCard(
        modifier = Modifier.widthIn(max = 720.dp).fillMaxWidth(),
        title = when {
            existing != null -> "Modifier la page"
            role == "home" -> "Page d'accueil · ${langName(lang)}"
            role == "about" -> "Page À propos · ${langName(lang)}"
            else -> "Nouvelle page"
        },
        icon = "edit",
        padding = PaddingValues(TeddSpace.S5),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            TeddField(label = "Titre", value = title, onValueChange = { title = it; if (!slugEdited) slug = slugify(it) }, placeholder = "Bonjour, je suis…")
            if (!preset.lockPlace) {
                Choice(
                    label = "Place sur le site",
                    options = listOf<Pair<String?, String>>(null to roleLabel(null), "home" to roleLabel("home"), "about" to roleLabel("about")),
                    selected = role,
                    onSelect = { role = it },
                )
                Choice(label = "Langue de cette page", options = LANGS, selected = lang, onSelect = { lang = it; translationOf = null })
            }
            TeddField(label = "Adresse sur le site", value = slug, onValueChange = { slug = slugify(it, typing = true); slugEdited = true }, placeholder = "ma-page")
            if (slug.isNotEmpty()) Hint("/$lang/${slugify(slug)}")
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                FormLabel("Texte")
                TextArea(body, { body = it }, placeholder = "Le texte de la page. Le Markdown est accepté.")
            }
            if (home || about) Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                FormLabel("Chiffres (optionnel)")
                Hint(
                    if (about) "Un par ligne, quatre au plus : le chiffre, une barre, puis ce qu'il compte. Sans chiffres, la page reprend ceux de l'accueil."
                    else "Un par ligne, quatre au plus : le chiffre, une barre, puis ce qu'il compte. Exemple : 8 ans | d'expérience",
                )
                TextArea(stats, { stats = it }, placeholder = "8 ans | d'expérience\n20 | projets livrés", minHeight = 96.dp)
            }
            if (home) HomeFields(extras)
            if (about) AboutFields(contact)
            if (!preset.lockPlace) TranslationPicker(
                candidates = publications.filter { it.isPage && it.lang != lang && it.id != existing?.id },
                selected = translationOf,
                onSelect = { translationOf = it },
            )
            FormError(model.formError)
            Row(
                modifier = Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
            ) {
                val ready = !model.busy && title.isNotBlank() && slug.isNotEmpty()
                TeddButton(
                    text = "Aperçu",
                    onClick = {
                        model.preview = PreviewDoc(
                            kind = "page", lang = lang, slug = slugify(slug).ifEmpty { "adresse" }, title = title, body = body,
                            stats = if (home || about) parseStats(stats) else emptyList(), role = role, draft = true,
                        )
                    },
                    kind = TeddButtonKind.Ghost,
                    icon = "eye",
                )
                TeddButton(text = "Mettre en ligne", onClick = { save(true) }, kind = TeddButtonKind.Publish, enabled = ready)
                TeddButton(text = "Enregistrer le brouillon", onClick = { save(false) }, enabled = ready)
                TeddButton(text = "Annuler", onClick = { model.form = null }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

/** La présentation de l'appli et le bandeau de contact, tels qu'on les saisit. */
internal class HomeState(initial: HomeExtras) {
    var appLead by mutableStateOf(initial.app_lead)
    var appPoints by mutableStateOf(initial.app_points.joinToString("\n"))
    var ctaTitle by mutableStateOf(initial.cta_title)
    var ctaBody by mutableStateOf(initial.cta_body)
    var ctaButton by mutableStateOf(initial.cta_button)

    fun value() = HomeExtras(
        app_lead = appLead.trim(),
        app_points = appPoints.lines().map { it.trim() }.filter { it.isNotEmpty() }.take(4),
        cta_title = ctaTitle.trim(),
        cta_body = ctaBody.trim(),
        cta_button = ctaButton.trim(),
    )
}

/** Le bloc `home` d'une page rouverte, ou rien. */
internal fun homeExtrasOf(page: ServerPublication?): HomeExtras =
    page?.content?.get("home")?.let { element ->
        runCatching { kotlinx.serialization.json.Json { ignoreUnknownKeys = true }.decodeFromJsonElement(HomeExtras.serializer(), element) }.getOrNull()
    } ?: HomeExtras()

@Composable
internal fun HomeFields(state: HomeState) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            FormLabel("Présentation de l'appli (optionnel)")
            Hint("Sous les téléchargements : ce qu'est teddApp en une ou deux phrases, puis ses arguments, un par ligne, quatre au plus. Vide, le site garde son texte.")
            TextArea(state.appLead, { state.appLead = it.take(300) }, placeholder = "L'application que je construis pour…", minHeight = 72.dp)
            TextArea(state.appPoints, { state.appPoints = it }, placeholder = "Fonctionne sans réseau\nSynchronisée entre tes appareils", minHeight = 96.dp)
        }
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            FormLabel("Bandeau de contact (optionnel)")
            Hint("Le bandeau sombre au bas de chaque page. Son bouton mène à la page À propos.")
            TeddField(label = "Titre", value = state.ctaTitle, onValueChange = { state.ctaTitle = it.take(80) }, placeholder = "Un projet en tête ?")
            TeddField(label = "Texte", value = state.ctaBody, onValueChange = { state.ctaBody = it.take(240) }, placeholder = "Architecture, IA ou produit : parlons-en.")
            TeddField(label = "Bouton", value = state.ctaButton, onValueChange = { state.ctaButton = it.take(40) }, placeholder = "Prendre contact")
        }
    }
}

/** Le contact de la page À propos, tel qu'on le saisit : les liens, un par ligne, « Libellé | adresse ». */
internal class AboutState(initial: AboutExtras) {
    var email by mutableStateOf(initial.email)
    var phone by mutableStateOf(initial.phone)
    var links by mutableStateOf(initial.links.joinToString("\n") { "${it.label} | ${it.url}" })

    fun value() = AboutExtras(
        email = email.trim(),
        phone = phone.trim(),
        links = links.lines().mapNotNull { line ->
            val url = line.substringAfterLast('|').trim()
            val label = if ('|' in line) line.substringBeforeLast('|').trim() else ""
            if (url.isEmpty()) null else AboutLink(label.ifEmpty { url.removePrefix("https://").removePrefix("http://").substringBefore('/') }, url)
        }.take(6),
    )
}

internal fun aboutExtrasOf(page: ServerPublication?): AboutExtras =
    page?.content?.get("about")?.let { element ->
        runCatching { kotlinx.serialization.json.Json { ignoreUnknownKeys = true }.decodeFromJsonElement(AboutExtras.serializer(), element) }.getOrNull()
    } ?: AboutExtras()

@Composable
internal fun AboutFields(state: AboutState) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        FormLabel("Contact (optionnel)")
        Hint("Le site en fait ses boutons « Écrire un e-mail » et « Appeler », et une rangée de liens.")
        TeddField(label = "E-mail", value = state.email, onValueChange = { state.email = it.trim().take(120) }, keyboardType = androidx.compose.ui.text.input.KeyboardType.Email, placeholder = "moi@exemple.org")
        TeddField(label = "Téléphone", value = state.phone, onValueChange = { state.phone = it.take(30) }, keyboardType = androidx.compose.ui.text.input.KeyboardType.Phone, placeholder = "+33 6 12 34 56 78")
        FormLabel("Liens")
        Hint("Un par ligne, six au plus : le libellé, une barre, puis l'adresse. Exemple : GitHub | https://github.com/moi")
        TextArea(state.links, { state.links = it }, placeholder = "GitHub | https://github.com/moi\nLinkedIn | https://www.linkedin.com/in/moi", minHeight = 80.dp)
    }
}

@Composable
internal fun TextArea(value: String, onValueChange: (String) -> Unit, placeholder: String, minHeight: androidx.compose.ui.unit.Dp = 180.dp) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    BasicTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = minHeight)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.LineStrong, shape)
            .padding(TeddSpace.S3),
        textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
        cursorBrush = SolidColor(TeddTokens.LimeStrong),
        decorationBox = { inner ->
            if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = TeddType.Body)
            inner()
        },
    )
}
